import type { IETicketMedia, TETicketMediaType } from '#/utils/eTicketMedia'
export type { IETicketMedia, TETicketMediaType }

/**
 * `awaiting_media` means Tranzer booked the ticket but has not produced its
 * files yet (DEV-3225). It is not finished, and it is not a failure either —
 * a retry job keeps looking, so it must not read as "New".
 */
export type TETicketStatus = 'new' | 'awaiting_media' | 'failed' | 'complete'

export type TETicketFilter = 'all' | TETicketStatus

export type TTicketType = 'one_way' | 'round_trip' | 'one_way_off_peak' | 'round_trip_off_peak'

export type TDeliveryMethod = 'wallet' | 'homeprint'

export type TLocationType = 'railway_station' | 'bus_station' | 'public_transport_stop' | 'other'

export interface ILocationResult {
  id: string
  name: string
  type: TLocationType
}

export type TPassengerType = 'adult' | 'child' | 'senior'

export interface IPassengerCounts {
  adult: number
  child: number
  senior: number
}

// FIXME: prices and age ranges are hard-coded placeholders. The spec
// (§6) says these should come from a contract / pricing endpoint per
// company. Swap when the backend exposes that endpoint.
export interface IPassengerCategory {
  type: TPassengerType
  /** Indicative price in cents — display only; the real total comes from the chosen quote. */
  indicativePriceCents: number | null
  /** Optional badge label, e.g. 'gratis' / 'korting'. */
  priceModifier?: 'free' | 'discount'
}

export const PASSENGER_CATEGORIES: IPassengerCategory[] = [
  { type: 'adult', indicativePriceCents: 1250 },
  { type: 'child', indicativePriceCents: 0, priceModifier: 'free' },
  { type: 'senior', indicativePriceCents: 950, priceModifier: 'discount' },
]

export interface IReturnTrip {
  date: string
  time: string
}

export interface ITraveller {
  type: TPassengerType
  firstName: string
  lastName: string
  email: string
  tag: string
}

export interface IJourneyLeg {
  departureStation: string
  arrivalStation: string
  departureTime: string
  arrivalTime: string
  operator: string
  // Structured modality from the BE: 'train' | 'bus' | 'tram' | 'metro' |
  // 'subway' | 'walking'. Use for icon selection only.
  mode: string
  // Human-readable label from Tranzer — e.g. 'Sprinter naar Amsterdam
  // Centraal', 'Intercity naar Den Haag CS', 'Walk to ...'. This is the
  // only field that distinguishes Sprinter from Intercity, so it MUST be
  // what we render to users for rail legs.
  description: string
}

export interface IQuoteOption {
  quoteId: string
  totalPrice: number
  currency: string
  departureTime: string
  arrivalTime: string
  durationMinutes: number
  transfers: number
  legs: IJourneyLeg[]
  ticketType: TTicketType
  conditions: string
  supplier: string
  conditionUrls: string[]
}

export interface IOrderFormState {
  fromStation: string
  fromStationName: string
  toStation: string
  toStationName: string
  travelDate: string
  travelTime: string
  trainClass: 1 | 2
  ticketType: TTicketType
  deliveryMethod: TDeliveryMethod
  selectedQuoteId: string
  selectedQuotePrice: number | null
  // Full quote snapshot captured at selection time. Lets downstream steps
  // (Terms, Review) render the chosen option without going back to
  // `useQuoteOptions` — which can refetch on `passengers` query-key
  // changes and would otherwise briefly return an empty list, blanking
  // the Review panel even though the user had clearly chosen something.
  // (Passenger-count changes also invalidate the snapshot itself, so
  // `handlePassengerCountChange` clears the selection to force a re-pick.)
  selectedOption: IQuoteOption | null
  passengerCounts: IPassengerCounts
  travellers: ITraveller[]
  /** Null until the user enables "Retour toevoegen". */
  returnTrip: IReturnTrip | null
  /** Picked quote for the return leg — only meaningful when `returnTrip` is set. */
  selectedReturnQuoteId: string
  selectedReturnQuotePrice: number | null
  /** Same snapshot pattern as `selectedOption`, for the return leg. */
  selectedReturnOption: IQuoteOption | null
}

export const EMPTY_TRAVELLER: ITraveller = {
  type: 'adult',
  firstName: '',
  lastName: '',
  email: '',
  tag: '',
}

export const createEmptyTraveller = (type: TPassengerType): ITraveller => ({
  ...EMPTY_TRAVELLER,
  type,
})

export const MAX_TRAVELLERS = 6

// Tranzer-side bookings take 60+ seconds to settle and the wallet pass /
// homeprint PDF generation runs asynchronously after that, so departures
// less than half an hour from now don't give the traveller enough margin.
// Validate against now + this many minutes.
export const MINIMUM_LEAD_MINUTES = 30

// Pre-fill the departure further into the future than the validation
// floor — the user spends a minute or two picking stations + travellers,
// and a 30-min default leaves zero margin once they reach the Options
// step. An hour gives them buffer without nudging at the floor.
export const DEFAULT_LEAD_MINUTES = 60

const formatDate = (date: Date): string => {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const formatTime = (date: Date): string => {
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${hh}:${min}`
}

// Threshold is the start of the minute exactly N minutes from now: zero
// out seconds/ms so that picking the displayed HH:MM (which the browser's
// time input rounds to) is treated as valid. Without this, a user
// selecting the pre-filled +30 value at, say, "14:35:42" with a min of
// "15:05:42" would parse as "15:05:00" — 42 seconds early — and the
// inline validation would fire on the exact value we just defaulted to.
export const getMinimumDepartureDate = (): Date => {
  const date = new Date()
  date.setMinutes(date.getMinutes() + MINIMUM_LEAD_MINUTES)
  date.setSeconds(0, 0)
  return date
}

// Same seconds-zeroing rationale as getMinimumDepartureDate — kept
// separate so the validation floor and the prefill default can drift
// independently.
export const getDefaultDepartureDate = (): Date => {
  const date = new Date()
  date.setMinutes(date.getMinutes() + DEFAULT_LEAD_MINUTES)
  date.setSeconds(0, 0)
  return date
}

/** Parse `YYYY-MM-DD` + `HH:MM` into a local-zone Date, or null if either side is empty/malformed. */
export const parseTravelDateTime = (date: string, time: string): Date | null => {
  if (!date || !time) return null
  const parsed = new Date(`${date}T${time}:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const createEmptyOrderForm = (): IOrderFormState => {
  const defaultDeparture = getDefaultDepartureDate()
  return {
    fromStation: '',
    fromStationName: '',
    toStation: '',
    toStationName: '',
    travelDate: formatDate(defaultDeparture),
    travelTime: formatTime(defaultDeparture),
    trainClass: 2,
    ticketType: 'one_way',
    deliveryMethod: 'wallet',
    selectedQuoteId: '',
    selectedQuotePrice: null,
    selectedOption: null,
    passengerCounts: { adult: 1, child: 0, senior: 0 },
    travellers: [createEmptyTraveller('adult')],
    returnTrip: null,
    selectedReturnQuoteId: '',
    selectedReturnQuotePrice: null,
    selectedReturnOption: null,
  }
}

export const totalPassengerCount = (counts: IPassengerCounts): number => counts.adult + counts.child + counts.senior

/** Whether a category currently has at least one selected traveller. */
export const hasPassengers = (counts: IPassengerCounts): boolean => totalPassengerCount(counts) > 0

export interface IETicket {
  id: string
  fromStation: string
  toStation: string
  fromStationName: string
  toStationName: string
  travellerEmail: string
  trainClass: 1 | 2
  ticketType: TTicketType
  ticketTypeLabel: string
  travelDate: string
  travelTime: string | null
  price: number | null
  status: string
  isKnownStatus: boolean
  statusLabel: string
  media: IETicketMedia[]
  createdAt: string | null
  tag: string
  deliveryMethod: TDeliveryMethod
  orderedBy: string | null
}

const ETICKET_STATUSES: TETicketStatus[] = ['new', 'awaiting_media', 'failed', 'complete']

export const isETicketStatus = (value: string): value is TETicketStatus =>
  ETICKET_STATUSES.includes(value as TETicketStatus)
